import React, { useState, useContext } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Grid,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Button,
  Icon,
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import { GlobalContext } from '../../global-context';
import moment from 'moment';
import { Link } from 'react-router-dom';
import BoothIdLink from '../reusable/BoothIdLink';

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    color: theme.palette.primary.dark,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    paddingBottom: theme.spacing(1),
    display: 'inline-block',
  },
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.08)',
  },
  mainPaper: {
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: 'none',
  },
  statsContainer: {
    marginBottom: theme.spacing(4),
  },
  statCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    padding: theme.spacing(2),
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
  },
  statValue: {
    fontWeight: 700,
    fontSize: '2.5rem',
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  boothItem: {
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    boxShadow: 'none',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderLeft: '4px solid transparent',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
  },
  statusIcon: {
    marginRight: theme.spacing(1),
    verticalAlign: 'middle',
  },
  statusChip: {
    margin: theme.spacing(0, 0.5, 0.5, 0),
  },
  inWarehouse: {
    backgroundColor: theme.palette.success.main,
    color: 'white',
    '& .MuiIcon-root': {
      color: 'white',
    },
  },
  inbounding: {
    backgroundColor: theme.palette.action.active,
    color: 'white',
    '& .MuiIcon-root': {
      color: 'white',
    },
  },
  outWithCustomer: {
    backgroundColor: theme.palette.info.main,
    color: 'white',
    '& .MuiIcon-root': {
      color: 'white',
    },
  },
  inTransit: {
    backgroundColor: theme.palette.warning.main,
    color: 'rgba(0, 0, 0, 0.87)',
    '& .MuiIcon-root': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  inactive: {
    backgroundColor: theme.palette.grey[500],
    color: 'white',
    '& .MuiIcon-root': {
      color: 'white',
    },
  },
  viewAllButton: {
    marginTop: theme.spacing(2),
  },
  detailsButton: {
    marginTop: theme.spacing(1),
  },
  tabsContainer: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  },
  tab: {
    color: theme.palette.primary.contrastText,
    opacity: 0.7,
    textTransform: 'none',
    fontWeight: 500,
    '&.Mui-selected': {
      opacity: 1,
      fontWeight: 600,
    },
  },
  warehouseBoothItem: {
    borderLeftColor: theme.palette.success.main,
  },
  inboundingBoothItem: {
    borderLeftColor: theme.palette.action.active,
  },
  customerBoothItem: {
    borderLeftColor: theme.palette.info.main,
  },
  transitBoothItem: {
    borderLeftColor: theme.palette.warning.main,
  },
  inactiveBoothItem: {
    borderLeftColor: theme.palette.grey[500],
  },
  refreshButton: {
    marginTop: theme.spacing(2),
    backgroundColor: theme.palette.primary.dark,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  highContrastBadges: {
    '& .MuiChip-root': {
      fontWeight: 600,
    },
    '& .MuiChip-icon': {
      color: 'inherit',
    },
  },
  filterContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filterButton: {
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1, 3),
    fontWeight: 500,
    textTransform: 'none',
    border: '2px solid transparent',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },
  },
  activeFilterButton: {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  warehouseFilterButton: {
    '&.active': {
      backgroundColor: theme.palette.success.main,
      borderColor: theme.palette.success.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.success.dark,
      },
    },
  },
  customerFilterButton: {
    '&.active': {
      backgroundColor: theme.palette.info.main,
      borderColor: theme.palette.info.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.info.dark,
      },
    },
  },
  transitFilterButton: {
    '&.active': {
      backgroundColor: theme.palette.warning.main,
      borderColor: theme.palette.warning.main,
      color: 'rgba(0, 0, 0, 0.87)',
      '&:hover': {
        backgroundColor: theme.palette.warning.dark,
        color: 'white',
      },
    },
  },
  inactiveFilterButton: {
    '&.active': {
      backgroundColor: theme.palette.grey[600],
      borderColor: theme.palette.grey[600],
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.grey[700],
      },
    },
  },
  allFilterButton: {
    '&.active': {
      backgroundColor: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  boothsContainer: {
    marginTop: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3),
  },
  titleSection: {
    flex: 1,
  },
  buttonSection: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  filterToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  filterControls: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchField: {
    width: '240px',
  },
  sortFormControl: {
    minWidth: 180,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`booth-tabpanel-${index}`}
      aria-labelledby={`booth-tab-${index}`}
      {...other}
    >
      {value === index && <Box className={props.className}>{children}</Box>}
    </div>
  );
}

const getAllBoothsWithStatusData = gql`
  query getAllBoothsWithStatusData {
    booths {
      id
      sequence
      key_number
      active
      battery_level
      build_date
      created
      updated
      activeOrder: orders(
        where: { status: { _nin: ["Done", "Cancelled", "Canceled"] }, active: { _eq: true } }
        limit: 1
        order_by: { event_date: asc }
      ) {
        id
        status
        event_name
        event_date
        return_date
        return_ship_date
        arrival_date
        ship_date
        outbound_tracking
        inbound_tracking
        outbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
        }
        inbound_shipment_activity {
          shipped
          delivered
          latest_status_type
          latest_status_description
        }
      }
      lastOrder: orders(where: { active: { _eq: true } }, order_by: { event_date: desc }, limit: 1) {
        id
        status
        event_name
        event_date
      }
    }
  }
`;

export default function Booths() {
  const classes = useStyles();
  const theme = useTheme();
  const ctx = useContext(GlobalContext);
  const [activeFilter, setActiveFilter] = useState('warehouse');
  const [sortBy, setSortBy] = useState('ship-date-asc');
  const [searchQuery, setSearchQuery] = useState('');

  const handleFilterChange = filter => {
    setActiveFilter(filter);
  };

  const handleSortChange = event => {
    setSortBy(event.target.value);
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  // Function to determine booth status
  const getBoothStatus = booth => {
    // No active order means booth is in warehouse
    if (!booth.activeOrder || booth.activeOrder.length === 0) {
      return 'InWarehouse';
    }

    // Get the active order
    const activeOrder = booth.activeOrder[0];

    // Check if order is Outbound but hasn't been dropped off yet based on UPS shipping cache
    if (
      activeOrder.status === 'Outbound' &&
      activeOrder.outbound_shipment_activity &&
      !activeOrder.outbound_shipment_activity.shipped
    ) {
      return 'OutboundPendingDropOff'; // New status for outbound orders not yet dropped off
    }

    // Outbound and in transit to customer
    if (
      activeOrder.status === 'Outbound' &&
      activeOrder.outbound_shipment_activity &&
      activeOrder.outbound_shipment_activity.shipped &&
      !activeOrder.outbound_shipment_activity.delivered
    ) {
      return 'InTransitToCustomer';
    }

    // Being returned but not inbounded yet
    if (
      (activeOrder.status === 'Inbound' || activeOrder.status === 'Returned') &&
      activeOrder.inbound_shipment_activity &&
      activeOrder.inbound_shipment_activity.shipped &&
      !activeOrder.inbound_shipment_activity.delivered
    ) {
      return 'InTransitToWarehouse';
    }

    // Returned but not inbounded yet (delivered but still in Returned status)
    if (
      activeOrder.status === 'Returned' &&
      activeOrder.inbound_shipment_activity &&
      activeOrder.inbound_shipment_activity.delivered
    ) {
      return 'InWarehouseReturnedPending';
    }

    // Check for Checkin status - booth should be categorized as in warehouse
    if (activeOrder.status === 'Checkin') {
      return 'InWarehouseCheckinPending';
    }

    // Otherwise booth is out with customer
    return 'OutWithCustomer';
  };

  // Function to get the status chip props
  const getStatusChipProps = status => {
    switch (status) {
      case 'InWarehouse':
        return {
          label: 'In Warehouse',
          className: classes.inWarehouse,
          icon: <Icon fontSize='small'>home</Icon>,
        };
      case 'OutboundPendingDropOff':
        return {
          label: 'Outbound - Pending Dropoff',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>local_shipping</Icon>,
        };
      case 'InTransitToCustomer':
        return {
          label: 'Outbound Shipping',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>local_shipping</Icon>,
        };
      case 'OutWithCustomer':
        return {
          label: 'With Customer',
          className: classes.outWithCustomer,
          icon: <Icon fontSize='small'>business</Icon>,
        };
      case 'InTransitToWarehouse':
        return {
          label: 'Inbound Shipping',
          className: classes.inTransit,
          icon: <Icon fontSize='small'>assignment_return</Icon>,
        };
      case 'InWarehouseReturnedPending':
        return {
          label: 'Returned (Needs checkin)',
          className: classes.inbounding,
          icon: <Icon fontSize='small'>assignment_turned_in</Icon>,
        };
      case 'InWarehouseCheckinPending':
        return {
          label: 'Returned (Needs checkin)',
          className: classes.inbounding,
          icon: <Icon fontSize='small'>assignment_turned_in</Icon>,
        };
      default:
        return {
          label: 'Inactive',
          className: classes.inactive,
          icon: <Icon fontSize='small'>block</Icon>,
        };
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleSection}>
          <Typography variant='h4' className={classes.title}>
            Booth Inventory
          </Typography>
        </div>
        <div className={classes.buttonSection}>
          <Query
            query={getAllBoothsWithStatusData}
            onError={err => {
              if (ctx.handleNotifications) {
                ctx.handleNotifications(true, 'error', 'Failed to retrieve booths data: ' + err.toString());
              }
            }}
          >
            {({ refetch }) => (
              <Button
                variant='contained'
                className={classes.refreshButton}
                startIcon={<Icon>refresh</Icon>}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            )}
          </Query>
        </div>
      </div>

      <Query
        query={getAllBoothsWithStatusData}
        onError={err => {
          if (ctx.handleNotifications) {
            ctx.handleNotifications(true, 'error', 'Failed to retrieve booths data: ' + err.toString());
          }
        }}
      >
        {result => {
          const { loading, error, data, refetch } = result;

          if (loading) {
            return (
              <CircularProgress
                style={{
                  display: 'block',
                  margin: 'auto',
                }}
              />
            );
          }

          if (error) {
            return (
              <Paper className={classes.paper}>
                <Typography color='error'>Error loading booth data: {error.message}</Typography>
                <Button variant='contained' color='primary' onClick={() => refetch()}>
                  Retry
                </Button>
              </Paper>
            );
          }

          const { booths = [] } = data;

          // Process booths data
          const boothsWithStatus = booths.map(booth => ({
            ...booth,
            status: getBoothStatus(booth),
          }));

          // Filter booths by status
          const inWarehouse = boothsWithStatus.filter(
            booth =>
              (booth.status === 'InWarehouse' ||
                booth.status === 'InWarehouseReturnedPending' ||
                booth.status === 'InWarehouseCheckinPending' ||
                booth.status === 'OutboundPendingDropOff') &&
              booth.active !== false
          );

          const outWithCustomer = boothsWithStatus.filter(
            booth =>
              booth.status === 'OutWithCustomer' ||
              booth.status === 'InTransitToCustomer' ||
              booth.status === 'InTransitToWarehouse'
          );

          const inTransit = boothsWithStatus.filter(
            booth => booth.status === 'InTransitToCustomer' || booth.status === 'InTransitToWarehouse'
          );

          const outOfService = booths.filter(booth => booth.active === false);

          // Determine which booths to display based on active filter
          let displayedBooths = [];

          switch (activeFilter) {
            case 'warehouse':
              displayedBooths = inWarehouse;
              break;
            case 'customer':
              displayedBooths = outWithCustomer.filter(b => b.status === 'OutWithCustomer');
              break;
            case 'transit':
              displayedBooths = inTransit;
              break;
            case 'inactive':
              displayedBooths = outOfService;
              break;
            case 'all':
              displayedBooths = boothsWithStatus;
              break;
            default:
              displayedBooths = inWarehouse;
          }

          // Apply search filter if present
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            displayedBooths = displayedBooths.filter(booth => {
              // Search by ID, sequence, or key number
              return (
                booth.id.toString().includes(query) ||
                (booth.sequence && booth.sequence.toString().toLowerCase().includes(query)) ||
                (booth.key_number && booth.key_number.toString().toLowerCase().includes(query))
              );
            });
          }

          // Sort booths based on sortBy value
          switch (sortBy) {
            case 'id-asc':
              displayedBooths.sort((a, b) => a.id - b.id);
              break;
            case 'id-desc':
              displayedBooths.sort((a, b) => b.id - a.id);
              break;
            case 'build-date-asc':
              displayedBooths.sort((a, b) => {
                if (!a.build_date) return 1;
                if (!b.build_date) return -1;
                return new Date(a.build_date).getTime() - new Date(b.build_date).getTime();
              });
              break;
            case 'build-date-desc':
              displayedBooths.sort((a, b) => {
                if (!a.build_date) return 1;
                if (!b.build_date) return -1;
                return new Date(b.build_date).getTime() - new Date(a.build_date).getTime();
              });
              break;
            case 'key-number-asc':
              displayedBooths.sort((a, b) => {
                if (!a.key_number) return 1;
                if (!b.key_number) return -1;
                return parseInt(a.key_number, 10) - parseInt(b.key_number, 10);
              });
              break;
            case 'key-number-desc':
              displayedBooths.sort((a, b) => {
                if (!a.key_number) return 1;
                if (!b.key_number) return -1;
                return parseInt(b.key_number, 10) - parseInt(a.key_number, 10);
              });
              break;
            case 'ship-date-asc':
              displayedBooths.sort((a, b) => {
                const aOrder = a.activeOrder && a.activeOrder.length > 0 ? a.activeOrder[0] : null;
                const bOrder = b.activeOrder && b.activeOrder.length > 0 ? b.activeOrder[0] : null;

                // If neither has an active order with ship date, they're equal
                if ((!aOrder || !aOrder.ship_date) && (!bOrder || !bOrder.ship_date)) return 0;
                // If only b has a ship date, it comes first
                if (!aOrder || !aOrder.ship_date) return 1;
                // If only a has a ship date, it comes first
                if (!bOrder || !bOrder.ship_date) return -1;

                // Compare the dates
                return new Date(aOrder.ship_date).getTime() - new Date(bOrder.ship_date).getTime();
              });
              break;
            case 'ship-date-desc':
              displayedBooths.sort((a, b) => {
                const aOrder = a.activeOrder && a.activeOrder.length > 0 ? a.activeOrder[0] : null;
                const bOrder = b.activeOrder && b.activeOrder.length > 0 ? b.activeOrder[0] : null;

                // If neither has an active order with ship date, they're equal
                if ((!aOrder || !aOrder.ship_date) && (!bOrder || !bOrder.ship_date)) return 0;
                // If only b has a ship date, it comes first
                if (!aOrder || !aOrder.ship_date) return 1;
                // If only a has a ship date, it comes first
                if (!bOrder || !bOrder.ship_date) return -1;

                // Compare the dates in reverse order
                return new Date(bOrder.ship_date).getTime() - new Date(aOrder.ship_date).getTime();
              });
              break;
            case 'event-date-asc':
              displayedBooths.sort((a, b) => {
                const aOrder = a.activeOrder && a.activeOrder.length > 0 ? a.activeOrder[0] : null;
                const bOrder = b.activeOrder && b.activeOrder.length > 0 ? b.activeOrder[0] : null;

                // If neither has an active order with event date, they're equal
                if ((!aOrder || !aOrder.event_date) && (!bOrder || !bOrder.event_date)) return 0;
                // If only b has an event date, it comes first
                if (!aOrder || !aOrder.event_date) return 1;
                // If only a has an event date, it comes first
                if (!bOrder || !bOrder.event_date) return -1;

                // Compare the dates
                return new Date(aOrder.event_date).getTime() - new Date(bOrder.event_date).getTime();
              });
              break;
            case 'event-date-desc':
              displayedBooths.sort((a, b) => {
                const aOrder = a.activeOrder && a.activeOrder.length > 0 ? a.activeOrder[0] : null;
                const bOrder = b.activeOrder && b.activeOrder.length > 0 ? b.activeOrder[0] : null;

                // If neither has an active order with event date, they're equal
                if ((!aOrder || !aOrder.event_date) && (!bOrder || !bOrder.event_date)) return 0;
                // If only b has an event date, it comes first
                if (!aOrder || !aOrder.event_date) return 1;
                // If only a has an event date, it comes first
                if (!bOrder || !bOrder.event_date) return -1;

                // Compare the dates in reverse order
                return new Date(bOrder.event_date).getTime() - new Date(aOrder.event_date).getTime();
              });
              break;
          }

          return (
            <>
              {/* Summary Statistics */}
              <Grid container spacing={3} className={classes.statsContainer}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>{inWarehouse.length}</Typography>
                      <Typography className={classes.statLabel}>Booths in Warehouse</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>
                        {outWithCustomer.filter(b => b.status === 'OutWithCustomer').length}
                      </Typography>
                      <Typography className={classes.statLabel}>Booths with Customers</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card className={classes.statCard}>
                    <CardContent>
                      <Typography className={classes.statValue}>{inTransit.length}</Typography>
                      <Typography className={classes.statLabel}>Booths In Transit</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Modern Filter UI */}
              <div className={classes.filterContainer}>
                <Button
                  className={`${classes.filterButton} ${classes.warehouseFilterButton} ${
                    activeFilter === 'warehouse' ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange('warehouse')}
                  startIcon={<Icon>home</Icon>}
                >
                  In Warehouse ({inWarehouse.length})
                </Button>
                <Button
                  className={`${classes.filterButton} ${classes.customerFilterButton} ${
                    activeFilter === 'customer' ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange('customer')}
                  startIcon={<Icon>business</Icon>}
                >
                  With Customers ({outWithCustomer.filter(b => b.status === 'OutWithCustomer').length})
                </Button>
                <Button
                  className={`${classes.filterButton} ${classes.transitFilterButton} ${
                    activeFilter === 'transit' ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange('transit')}
                  startIcon={<Icon>local_shipping</Icon>}
                >
                  In Transit ({inTransit.length})
                </Button>
                <Button
                  className={`${classes.filterButton} ${classes.inactiveFilterButton} ${
                    activeFilter === 'inactive' ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange('inactive')}
                  startIcon={<Icon>block</Icon>}
                >
                  Out of Service ({outOfService.length})
                </Button>
                <Button
                  className={`${classes.filterButton} ${classes.allFilterButton} ${
                    activeFilter === 'all' ? 'active' : ''
                  }`}
                  onClick={() => handleFilterChange('all')}
                  startIcon={<Icon>view_list</Icon>}
                >
                  All Booths ({booths.length})
                </Button>
              </div>

              {/* Filtering and Sorting Controls */}
              <div className={classes.filterToolbar}>
                <div className={classes.filterControls}>
                  <TextField
                    className={classes.searchField}
                    label='Search Booths'
                    variant='outlined'
                    size='small'
                    value={searchQuery}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <Icon>search</Icon>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <FormControl variant='outlined' size='small' className={classes.sortFormControl}>
                    <InputLabel id='sort-by-label'>Sort By</InputLabel>
                    <Select
                      labelId='sort-by-label'
                      id='sort-by'
                      value={sortBy}
                      onChange={handleSortChange}
                      label='Sort By'
                    >
                      <MenuItem value='id-asc'>ID (Ascending)</MenuItem>
                      <MenuItem value='id-desc'>ID (Descending)</MenuItem>
                      <MenuItem value='build-date-asc'>Build Date (Oldest first)</MenuItem>
                      <MenuItem value='build-date-desc'>Build Date (Newest first)</MenuItem>
                      <MenuItem value='key-number-asc'>Key Number (Ascending)</MenuItem>
                      <MenuItem value='key-number-desc'>Key Number (Descending)</MenuItem>
                      <MenuItem value='ship-date-asc'>Ship Date (Soonest first)</MenuItem>
                      <MenuItem value='ship-date-desc'>Ship Date (Latest first)</MenuItem>
                      <MenuItem value='event-date-asc'>Event Date (Soonest first)</MenuItem>
                      <MenuItem value='event-date-desc'>Event Date (Latest first)</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Typography variant='body2' color='textSecondary'>
                  Showing {displayedBooths.length} {displayedBooths.length === 1 ? 'booth' : 'booths'}
                </Typography>
              </div>

              {/* Booth Grid */}
              <div className={classes.boothsContainer}>
                <Grid container spacing={3}>
                  {displayedBooths.map(booth => {
                    const statusChipProps = getStatusChipProps(booth.status);

                    // Get the appropriate border class based on status
                    let borderClass = '';
                    if (booth.active === false) {
                      borderClass = classes.inactiveBoothItem;
                    } else if (booth.status === 'InWarehouse') {
                      borderClass = classes.warehouseBoothItem;
                    } else if (
                      booth.status === 'InWarehouseReturnedPending' ||
                      booth.status === 'InWarehouseCheckinPending'
                    ) {
                      borderClass = classes.inboundingBoothItem;
                    } else if (booth.status === 'OutWithCustomer') {
                      borderClass = classes.customerBoothItem;
                    } else if (
                      booth.status === 'InTransitToCustomer' ||
                      booth.status === 'InTransitToWarehouse' ||
                      booth.status === 'OutboundPendingDropOff'
                    ) {
                      borderClass = classes.transitBoothItem;
                    }

                    const activeOrder = booth.activeOrder && booth.activeOrder.length > 0 ? booth.activeOrder[0] : null;

                    return (
                      <Grid item xs={12} sm={6} md={4} key={booth.id}>
                        <Paper className={`${classes.paper} ${classes.boothItem} ${borderClass}`}>
                          <Link to={`/booths/${booth.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Box mb={1}>
                              <BoothIdLink boothId={booth.id} linkToDetail={false} />
                            </Box>
                            <Chip
                              icon={statusChipProps.icon}
                              label={statusChipProps.label}
                              className={`${classes.statusChip} ${statusChipProps.className} ${classes.highContrastBadges}`}
                              size='small'
                            />
                            {booth.active === false && (
                              <Chip
                                icon={<Icon fontSize='small'>cancel</Icon>}
                                label='Out of Service'
                                className={`${classes.statusChip} ${classes.inactive} ${classes.highContrastBadges}`}
                                size='small'
                                style={{ marginLeft: '8px' }}
                              />
                            )}
                            <Divider style={{ margin: '8px 0' }} />

                            {activeOrder ? (
                              <>
                                <Typography variant='body2' color='textSecondary'>
                                  Current Order: #{activeOrder.id} - {activeOrder.event_name}
                                </Typography>
                                <Typography variant='body2' color='textSecondary'>
                                  Status: {activeOrder.status}
                                </Typography>

                                {/* Contextual Date Information */}
                                {booth.status === 'InWarehouse' || booth.status === 'OutboundPendingDropOff' ? (
                                  <Typography variant='body2' color='textSecondary'>
                                    <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                      local_shipping
                                    </Icon>
                                    Ship Date:{' '}
                                    {activeOrder.ship_date ? (
                                      moment(activeOrder.ship_date).isAfter(moment()) ? (
                                        <>
                                          <span style={{ fontWeight: 600, color: theme.palette.success.dark }}>
                                            {moment(activeOrder.ship_date).fromNow()}
                                            {moment(activeOrder.ship_date).diff(moment(), 'days') < 7 &&
                                              ` (${moment(activeOrder.ship_date).format('ddd')})`}
                                          </span>
                                          <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                            {' '}
                                            {moment(activeOrder.ship_date).format('MM/DD/YYYY')}
                                          </span>
                                        </>
                                      ) : (
                                        moment(activeOrder.ship_date).format('MM/DD/YYYY')
                                      )
                                    ) : (
                                      'No date set'
                                    )}
                                  </Typography>
                                ) : booth.status === 'OutWithCustomer' ? (
                                  <Typography variant='body2' color='textSecondary'>
                                    <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                      event
                                    </Icon>
                                    Event Date:{' '}
                                    {activeOrder.event_date ? (
                                      moment(activeOrder.event_date).isAfter(moment()) ? (
                                        <>
                                          <span style={{ fontWeight: 600, color: theme.palette.info.dark }}>
                                            {moment(activeOrder.event_date).fromNow()}
                                            {moment(activeOrder.event_date).diff(moment(), 'days') < 7 &&
                                              ` (${moment(activeOrder.event_date).format('ddd')})`}
                                          </span>
                                          <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                            {' '}
                                            {moment(activeOrder.event_date).format('MM/DD/YYYY')}
                                          </span>
                                        </>
                                      ) : (
                                        <span style={{ color: theme.palette.error.main }}>
                                          {moment(activeOrder.event_date).format('MM/DD/YYYY')} (Past)
                                        </span>
                                      )
                                    ) : (
                                      'Unknown'
                                    )}
                                  </Typography>
                                ) : booth.status === 'InTransitToWarehouse' ? (
                                  <Typography variant='body2' color='textSecondary'>
                                    <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                      assignment_return
                                    </Icon>
                                    Return Date:{' '}
                                    {activeOrder.return_date ? (
                                      moment(activeOrder.return_date).isAfter(moment()) ? (
                                        <>
                                          <span style={{ fontWeight: 600, color: theme.palette.warning.dark }}>
                                            {moment(activeOrder.return_date).fromNow()}
                                            {moment(activeOrder.return_date).diff(moment(), 'days') < 7 &&
                                              ` (${moment(activeOrder.return_date).format('ddd')})`}
                                          </span>
                                          <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                            {' '}
                                            {moment(activeOrder.return_date).format('MM/DD/YYYY')}
                                          </span>
                                        </>
                                      ) : (
                                        moment(activeOrder.return_date).format('MM/DD/YYYY')
                                      )
                                    ) : (
                                      'No date set'
                                    )}
                                  </Typography>
                                ) : (
                                  <Typography variant='body2' color='textSecondary'>
                                    Event Date:{' '}
                                    {activeOrder.event_date ? (
                                      moment(activeOrder.event_date).isAfter(moment()) ? (
                                        <>
                                          <span style={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                            {moment(activeOrder.event_date).fromNow()}
                                            {moment(activeOrder.event_date).diff(moment(), 'days') < 7 &&
                                              ` (${moment(activeOrder.event_date).format('ddd')})`}
                                          </span>
                                          <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                            {' '}
                                            {moment(activeOrder.event_date).format('MM/DD/YYYY')}
                                          </span>
                                        </>
                                      ) : (
                                        moment(activeOrder.event_date).format('MM/DD/YYYY')
                                      )
                                    ) : (
                                      'Unknown'
                                    )}
                                  </Typography>
                                )}

                                {/* Additional date information for InTransitToWarehouse booths */}
                                {booth.status === 'InTransitToWarehouse' && (
                                  <>
                                    <Typography variant='body2' color='textSecondary'>
                                      <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                        local_shipping
                                      </Icon>
                                      Expected Dropoff:{' '}
                                      {activeOrder.return_ship_date ? (
                                        moment(activeOrder.return_ship_date).isAfter(moment()) ? (
                                          <>
                                            <span style={{ fontWeight: 600, color: theme.palette.info.dark }}>
                                              {moment(activeOrder.return_ship_date).fromNow()}
                                              {moment(activeOrder.return_ship_date).diff(moment(), 'days') < 7 &&
                                                ` (${moment(activeOrder.return_ship_date).format('ddd')})`}
                                            </span>
                                            <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                              {' '}
                                              {moment(activeOrder.return_ship_date).format('MM/DD/YYYY')}
                                            </span>
                                          </>
                                        ) : (
                                          moment(activeOrder.return_ship_date).format('MM/DD/YYYY')
                                        )
                                      ) : (
                                        'Not scheduled'
                                      )}
                                    </Typography>
                                    <Typography variant='body2' color='textSecondary'>
                                      <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                        home
                                      </Icon>
                                      Expected Arrival:{' '}
                                      {activeOrder.return_date ? (
                                        moment(activeOrder.return_date).isAfter(moment()) ? (
                                          <>
                                            <span style={{ fontWeight: 600, color: theme.palette.success.dark }}>
                                              {moment(activeOrder.return_date).fromNow()}
                                              {moment(activeOrder.return_date).diff(moment(), 'days') < 7 &&
                                                ` (${moment(activeOrder.return_date).format('ddd')})`}
                                            </span>
                                            <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                              {' '}
                                              {moment(activeOrder.return_date).format('MM/DD/YYYY')}
                                            </span>
                                          </>
                                        ) : (
                                          moment(activeOrder.return_date).format('MM/DD/YYYY')
                                        )
                                      ) : (
                                        'Not scheduled'
                                      )}
                                    </Typography>
                                  </>
                                )}

                                {/* Additional date information for InTransitToCustomer booths */}
                                {booth.status === 'InTransitToCustomer' && (
                                  <>
                                    <Typography variant='body2' color='textSecondary'>
                                      <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                        business
                                      </Icon>
                                      Expected Arrival:{' '}
                                      {activeOrder.arrival_date ? (
                                        moment(activeOrder.arrival_date).isAfter(moment()) ? (
                                          <>
                                            <span style={{ fontWeight: 600, color: theme.palette.info.dark }}>
                                              {moment(activeOrder.arrival_date).fromNow()}
                                              {moment(activeOrder.arrival_date).diff(moment(), 'days') < 7 &&
                                                ` (${moment(activeOrder.arrival_date).format('ddd')})`}
                                            </span>
                                            <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                              {' '}
                                              {moment(activeOrder.arrival_date).format('MM/DD/YYYY')}
                                            </span>
                                          </>
                                        ) : (
                                          moment(activeOrder.arrival_date).format('MM/DD/YYYY')
                                        )
                                      ) : (
                                        'Not scheduled'
                                      )}
                                    </Typography>
                                    <Typography variant='body2' color='textSecondary'>
                                      <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                        event
                                      </Icon>
                                      Event Date:{' '}
                                      {activeOrder.event_date ? (
                                        moment(activeOrder.event_date).isAfter(moment()) ? (
                                          <>
                                            <span style={{ fontWeight: 600, color: theme.palette.success.dark }}>
                                              {moment(activeOrder.event_date).fromNow()}
                                              {moment(activeOrder.event_date).diff(moment(), 'days') < 7 &&
                                                ` (${moment(activeOrder.event_date).format('ddd')})`}
                                            </span>
                                            <span style={{ color: theme.palette.text.secondary, fontSize: '0.9em' }}>
                                              {' '}
                                              {moment(activeOrder.event_date).format('MM/DD/YYYY')}
                                            </span>
                                          </>
                                        ) : (
                                          moment(activeOrder.event_date).format('MM/DD/YYYY')
                                        )
                                      ) : (
                                        'Not scheduled'
                                      )}
                                    </Typography>
                                  </>
                                )}

                                {/* Secondary Date Information */}
                                {booth.status === 'OutWithCustomer' &&
                                  activeOrder.event_date &&
                                  moment(activeOrder.event_date).isBefore(moment()) && (
                                    <Typography variant='body2' style={{ color: theme.palette.warning.dark }}>
                                      <Icon fontSize='small' style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                                        assignment_return
                                      </Icon>
                                      Expected Return:{' '}
                                      {activeOrder.return_date
                                        ? moment(activeOrder.return_date).format('MM/DD/YYYY')
                                        : 'No date set'}
                                    </Typography>
                                  )}
                              </>
                            ) : (
                              <>
                                <Typography variant='body2' color='textSecondary'>
                                  Last Order:{' '}
                                  {booth.lastOrder && booth.lastOrder.length > 0
                                    ? `#${booth.lastOrder[0].id} - ${booth.lastOrder[0].event_name}`
                                    : 'None'}
                                </Typography>
                                <Typography variant='body2' color='textSecondary'>
                                  Build Date:{' '}
                                  {booth.build_date ? moment(booth.build_date).format('MM/DD/YYYY') : 'Unknown'}
                                </Typography>
                                <Typography variant='body2' color='textSecondary'>
                                  Key Number: {booth.key_number}
                                </Typography>
                              </>
                            )}
                          </Link>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>

                {displayedBooths.length === 0 && (
                  <Typography variant='body1' align='center' style={{ padding: '24px 0' }}>
                    No booths found matching this filter.
                  </Typography>
                )}
              </div>
            </>
          );
        }}
      </Query>
    </div>
  );
}
